import { useEffect, useRef } from 'react';
import type { Scenario } from '../types';
import type { GenerationModel } from '../../../../features/generation-models/api';
import { useSceneHandlers } from './SceneHandlersContext';
import { useSceneStateContext } from './SceneStateContext';
import { isLyricsBgmPending } from './useBgmEditor';
import { useToast } from '../../../../hooks/useToast';
import { videoModelButtonLabel, videoModelGroupLabel } from '../../common/VideoModelFamilySelector';
import {
  LINK_PREVIOUS_MODE_EXTEND,
  LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V,
  resolveLinkPreviousMode,
  resolveUsableVideoModel,
  type LinkPreviousMode,
} from '../utils';

export type AutoVideoSegment = { start: number; end: number; seconds: number };

/** モデルの supported_durations が未設定のときの対応尺フォールバック (SceneItem と同値) */
const FALLBACK_DURATIONS = [4, 5, 6, 7, 8, 9, 10];

/**
 * モデルの対応尺 (正の値のみ・昇順)。supported_durations が空ならフォールバック。
 */
export function resolveSupportedDurations(model: Pick<GenerationModel, 'supported_durations'> | null | undefined): number[] {
  const supported = (model?.supported_durations ?? []).filter((d) => d > 0).sort((a, b) => a - b);
  return supported.length > 0 ? supported : FALLBACK_DURATIONS;
}

/**
 * 合計秒数をカバーできる最小の対応尺に丸める (無ければモデル最大)。
 * useSceneTimeline.getStoryboardRangeDuration と同じ丸め規則。
 */
export function roundToSupportedDuration(seconds: number, durations: number[]): number {
  if (durations.length === 0) return Math.max(1, Math.round(seconds));
  return durations.find((d) => d >= seconds) ?? durations[durations.length - 1];
}

/**
 * 本編シーンを動画カードの担当範囲へ分割する。
 *
 * 本数は「総尺 ÷ 最大尺」の切り上げで先に決め (例: 30秒 ÷ 15秒 = 2本)、その本数に
 * 収まるようシーンを連続したまま配分する (各セグメントの合計尺の最大値を最小化する
 * 線形分割)。先頭から貪欲に詰めると 4,4,4,4,4,4,3,3 秒 (計30秒) が 12/12/6 の 3本に
 * 割れてしまうため、最小本数を優先する。同じ最大値なら前のセグメントを大きくする。
 * 配分しても最大尺に収まらないセグメント (単独で最大尺を超えるシーンを含む等) は、
 * 尺がモデル最大に丸まる (roundToSupportedDuration)。
 * 入力は order 昇順の本編シーンとその実効コマ尺。
 */
export function segmentScenesByMaxDuration(
  scenes: Array<{ order: number; seconds: number }>,
  maxDuration: number,
): AutoVideoSegment[] {
  const n = scenes.length;
  if (n === 0) return [];
  const total = scenes.reduce((acc, s) => acc + s.seconds, 0);
  const count = maxDuration > 0 ? Math.min(n, Math.max(1, Math.ceil(total / maxDuration))) : 1;

  // prefix[i] = 先頭 i シーンの合計尺
  const prefix: number[] = [0];
  for (const s of scenes) prefix.push(prefix[prefix.length - 1] + s.seconds);
  const sumOf = (from: number, to: number) => prefix[to] - prefix[from];

  // best[j][i] = 先頭 i シーンを j セグメントに分けたときの「最大セグメント尺」の最小値。
  // cut[j][i] = そのときの最後のセグメントの開始インデックス。
  const best: number[][] = Array.from({ length: count + 1 }, () => Array<number>(n + 1).fill(Infinity));
  const cut: number[][] = Array.from({ length: count + 1 }, () => Array<number>(n + 1).fill(0));
  for (let i = 1; i <= n; i += 1) best[1][i] = sumOf(0, i);
  for (let j = 2; j <= count; j += 1) {
    for (let i = j; i <= n; i += 1) {
      // 最後のセグメントを [m, i) にする。m 昇順で「以下」更新 = 同点なら m が大きい方
      // (= 前のセグメントを大きく) を採る。
      for (let m = j - 1; m < i; m += 1) {
        const candidate = Math.max(best[j - 1][m], sumOf(m, i));
        if (candidate <= best[j][i]) {
          best[j][i] = candidate;
          cut[j][i] = m;
        }
      }
    }
  }

  // 後ろから区切り位置を辿ってセグメントに起こす
  const boundaries: number[] = [n];
  let i = n;
  for (let j = count; j >= 2; j -= 1) {
    i = cut[j][i];
    boundaries.unshift(i);
  }
  const segments: AutoVideoSegment[] = [];
  let from = 0;
  for (const to of boundaries) {
    if (to <= from) continue;
    segments.push({ start: scenes[from].order, end: scenes[to - 1].order, seconds: sumOf(from, to) });
    from = to;
  }
  return segments;
}

/**
 * 既存のセグメント一覧に、分割し直したセグメント群を差し込む。
 * 分割元カードの担当範囲 [start, end] と開始 order が重なる既存エントリは新しい
 * セグメントに置き換え、それ以外は残す。結果は start 昇順。
 */
export function mergeSegments(
  existing: AutoVideoSegment[],
  replaced: { start: number; end: number },
  next: AutoVideoSegment[],
): AutoVideoSegment[] {
  const kept = existing.filter((seg) => seg.start < replaced.start || seg.start > replaced.end);
  return [...kept, ...next].sort((a, b) => a.start - b.start);
}

/** 自動分割で生まれた 2 本目以降のカードに書き込む「前の動画とつなげる」の初期値。
 *
 * 分割は「モデルの最大尺に収まらない」という技術的な都合で 1 本の動画を切っただけ
 * なので、分割後のカードは既定で前のカードとつなげる (docs/features/
 * link-previous-scene-continuity-default-plan.md 第2弾・2026-09-14)。場面継続の
 * 読み出し時導出 (deriveLinkPreviousVideoSetting) と違い、分割境界が場面転換でも ON に
 * する。ただし境界の扱いはつなぎ方式で変える:
 *  - frame_anchor_v2v (Seedance 2.5系): 境界が場面転換 (アンカーシーンが
 *    continues_previous_shot でない) なら、前の動画のラストフレームを first frame に
 *    固定すると転換先の場面に合わないため、静止画フレームを除外 (§11) して参照動画
 *    (v2v) だけでスタイル・被写体をそろえる
 *  - v2v_only (MiniMax H3): フレームアンカー自体が無いので常に ON
 *  - extend (Gemini Omni): ネイティブ延長は必ず前の動画の続きから始まるため、境界が
 *    場面転換なら ON にしない (null = 書き込まず、従来どおり単独生成)
 * アンカーシーンに明示値 (link_previous_video が boolean) が既にあれば尊重して null。
 * つなぎ方式が解決できないモデル (Seedance 2.0 等) も null。
 */
export function autoSplitLinkPreviousSettings(
  anchorScene: { continues_previous_shot?: boolean | null; link_previous_video?: boolean | null },
  linkPrevMode: LinkPreviousMode | null,
): { linkPreviousVideo: true; linkPreviousFrameExcluded?: true } | null {
  if (!linkPrevMode) return null;
  if (typeof anchorScene.link_previous_video === 'boolean') return null;
  const continues = anchorScene.continues_previous_shot === true;
  if (linkPrevMode === LINK_PREVIOUS_MODE_EXTEND) {
    return continues ? { linkPreviousVideo: true } : null;
  }
  if (linkPrevMode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V && !continues) {
    return { linkPreviousVideo: true, linkPreviousFrameExcluded: true };
  }
  return { linkPreviousVideo: true };
}

// シナリオごとの分割結果。空配列 = 「判定済み・自動分割は適用しない」。
// WorkPanel の再マウント (タブ切替・フェーズ往復) で state が消えても分割結果と
// 各カードの既定範囲を保てるよう、モジュールスコープに置く (リロードで再判定)。
const autoSegmentCache = new Map<string, AutoVideoSegment[]>();

/**
 * ストーリーボードモードの動画カード自動分割。
 *
 * (A) 初期分割: シナリオ編集画面を開いた時点で、タイムライン総尺 (本編シーンのコマ尺
 * 合計) が動画モデルの最大対応尺を超えている場合、総尺が収まる数の動画カードを最初から
 * 並べておく (従来はユーザーが「動画を追加」を押して手で分割していた)。
 *
 * (B) モデル切替時の再分割: 生成前の動画カードで実効モデルが切り替わり、担当範囲
 * (「特定シーンだけ生成」の範囲。未設定なら本編全体) の合計尺が新モデルの最大尺を超えた
 * 場合、そのカードの範囲を先頭セグメントへ縮めて残りを追加カードとして並べ、トーストで
 * 知らせる。これが無いと、例えば 30秒のシナリオを Seedance 2.5 (最大30秒) で組んだ後に
 * Seedance 2.0 (最大15秒) へ切り替えると、尺だけ 15秒へスナップされて 30秒分のシーンが
 * 1本の15秒動画に押し込められてしまう。
 *
 * このフックは「セグメント分割の計算」と「追加カード (extraVideoCardOrders) の
 * 材料化」を行う。追加カードの「特定シーンだけ生成」範囲はここでは書き込まず、
 * WorkPanel が videoCardDefaultRangeStart/End として各カードへ渡し、SceneItem の
 * 復元 effect (条件駆動・範囲未設定の間は何度でも再試行する) が適用する。
 * (B) の分割元カードだけは既にマウント済みで復元経路が使えないため、範囲を直接書き込む。
 *
 * 初期分割の判定はシナリオごとに 1 回だけ・まっさらな状態でのみ行う:
 *  - 既に動画を持つシーンがある / 「動画を追加」済み / カード1の範囲をユーザーが
 *    操作済み / 保存済み scene_range がある場合は「適用しない」と記録する
 *  - 追加カードの材料化は WorkPanel のマウントごとに 1 回。適用後にユーザーが
 *    カードを削除しても同一マウント中は再追加しない
 */
/**
 * 自動分割の記録を捨てて、次のレンダーで初期分割をやり直せるようにする (歌 → 映像の尺同期で
 * コマ尺が書き換わったとき。計画 md §5.5.1)。「まっさら」条件 (既存動画・追加カード・
 * ユーザー操作済み範囲が無い) はそのまま効くので、既に複数カードに分かれているシナリオは
 * 再分割されない (その場合は範囲を手で直す)。
 */
export function resetAutoVideoCardSegmentation(scenarioId: string): void {
  autoSegmentCache.delete(scenarioId);
}

export function useAutoVideoCardSegmentation({
  scenarios,
  activeScenarioId,
  videoGenerationMode,
  videoModels,
  defaultVideoModel,
  extraVideoCardOrders,
  setExtraVideoCardOrders,
}: {
  scenarios: Scenario[];
  activeScenarioId: string | null;
  videoGenerationMode: string | null;
  videoModels: GenerationModel[];
  defaultVideoModel: string | undefined;
  extraVideoCardOrders: Record<string, number[]>;
  setExtraVideoCardOrders: React.Dispatch<React.SetStateAction<Record<string, number[]>>>;
}): {
  /** 自動分割が適用されたシナリオの、アンカー order → 担当範囲。無ければ null。 */
  getAutoSegment: (scenarioId: string, anchorOrder: number) => AutoVideoSegment | null;
} {
  const { getSceneVideoSettings, updateSceneVideoSettings, getSceneStoryboardDuration } = useSceneHandlers();
  const { sceneVideoMaterialId, sceneVideoLoading } = useSceneStateContext();
  const toast = useToast();

  // このマウント中に追加カードを材料化済みのシナリオ id (削除の再追加防止)
  const materializedRef = useRef<Set<string>>(new Set());
  // 各動画カード (sceneKey) の直前の実効モデル。(B) の切替検知に使う。
  const previousCardModelRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (videoGenerationMode !== 'storyboard') return;
    const scenarioId = activeScenarioId ?? scenarios[0]?.id;
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario || !scenario.scenes || scenario.scenes.length === 0) return;

    // 本編シーン (ブランドカットのタイトル/エンドカードは本編動画の対象外)
    const bodyScenes = scenario.scenes
      .filter((s) => s.brand_cut_position !== 'start' && s.brand_cut_position !== 'end')
      .slice()
      .sort((a, b) => a.order - b.order);
    if (bodyScenes.length === 0) {
      if (!autoSegmentCache.has(scenario.id)) autoSegmentCache.set(scenario.id, []);
      return;
    }
    const keyOf = (order: number) => `${scenario.id}-scene-${order}`;
    const firstBodyOrder = bodyScenes[0].order;

    // シーンの実効コマ尺 (SceneItem/useSceneTimeline と同じ: 未編集分は DB 値、
    // クランプ上限は想定尺 (generation_metadata.duration) or 3秒)
    const durationMeta = /(\d+)/.exec(String((scenario.generation_metadata as Record<string, unknown> | undefined)?.duration ?? ''));
    const cutMax = durationMeta && Number(durationMeta[1]) > 0 ? Number(durationMeta[1]) : 3;
    const effDur = (s: Scenario['scenes'][number]) => Math.min(
      getSceneStoryboardDuration(keyOf(s.order), s.storyboard_duration_seconds ?? undefined),
      cutMax,
    );
    const resolveCardModel = (key: string) => {
      const code = resolveUsableVideoModel(getSceneVideoSettings(key).videoModel, defaultVideoModel);
      return videoModels.find((m) => m.model_code === code) ?? null;
    };
    const hasVideo = (s: Scenario['scenes'][number]) => !!(s.video_material_id || sceneVideoMaterialId[keyOf(s.order)]);

    // 歌モノ (計画 md §5.5.1): 歌 (BGM) の実測でコマ尺が書き戻されるまで初期分割を後回しにする。
    // キャッシュにも書かない (= 採用後のレンダーで再実行される) ので、カード境界が歌の
    // セクション境界に乗る。動画生成側も同じ判定 (isLyricsBgmPending) でゲートされている。
    if (!autoSegmentCache.has(scenario.id) && isLyricsBgmPending(scenario)) return;

    // ── 1. 初期分割の判定 (シナリオごとに 1 回だけ) ──
    if (!autoSegmentCache.has(scenario.id)) {
      const card1Key = keyOf(firstBodyOrder);

      // カード1の実効モデル (シーン上書き > シナリオ既定)。モデル一覧が未取得の間は
      // 最大尺を判定できないため、記録せずに取得後の再実行を待つ。
      const model = resolveCardModel(card1Key);
      if (!model) return;

      // まっさらな状態でのみ適用 (既存動画・追加済みカード・ユーザー操作・保存済み範囲を尊重)
      const card1Settings = getSceneVideoSettings(card1Key);
      const card1SavedRange = (bodyScenes[0].generation_metadata as { scene_range?: unknown } | undefined)?.scene_range;
      if (
        bodyScenes.some(hasVideo)
        || (extraVideoCardOrders[scenario.id] ?? []).length > 0
        || card1Settings.sceneRangeEnabled !== undefined
        || card1SavedRange != null
      ) {
        autoSegmentCache.set(scenario.id, []);
        return;
      }

      const durations = resolveSupportedDurations(model);
      const maxDuration = durations[durations.length - 1];
      const segments = segmentScenesByMaxDuration(
        bodyScenes.map((s) => ({ order: s.order, seconds: effDur(s) })),
        maxDuration,
      );
      autoSegmentCache.set(scenario.id, segments.length > 1 ? segments : []);
      // 2 本目以降のカードは既定で前のカードとつなげる (autoSplitLinkPreviousSettings)。
      // 新設カードはモデル上書きを持たないためカード1と同じモデルで方式を解決する。
      // updateSceneVideoSettings が scenes.link_previous_video へ永続化するので、
      // リロード後 (初期分割は再適用されない) も保存値として残る。
      if (segments.length > 1) {
        const linkPrevMode = resolveLinkPreviousMode(model.model_code, videoModels);
        for (const seg of segments.slice(1)) {
          const anchor = bodyScenes.find((s) => s.order === seg.start);
          const linkSettings = anchor ? autoSplitLinkPreviousSettings(anchor, linkPrevMode) : null;
          if (linkSettings) updateSceneVideoSettings(keyOf(seg.start), linkSettings);
        }
      }
    }

    // ── 2. 追加カードの材料化 (マウントごとに 1 回) ──
    // カード1 (最初の本編シーン) は常に存在するので、それ以外のセグメント開始を追加カードにする。
    const segments = autoSegmentCache.get(scenario.id) ?? [];
    const extraAnchors = segments.map((seg) => seg.start).filter((order) => order !== firstBodyOrder);
    if (extraAnchors.length > 0 && !materializedRef.current.has(scenario.id)) {
      materializedRef.current.add(scenario.id);
      setExtraVideoCardOrders((prev) => {
        const existing = prev[scenario.id] ?? [];
        // 既に動画を持つアンカーは自動カード化されるため追加不要
        const wanted = extraAnchors.filter((order) => !existing.includes(order));
        if (wanted.length === 0) return prev;
        return { ...prev, [scenario.id]: [...existing, ...wanted] };
      });
    }

    // ── 3. モデル切替時の再分割 ──
    // 動画カード = カード1 + 動画を持つ本編シーン + 「動画を追加」のローカルカード
    // (WorkPanel の anchorScenes と同じ導出)。
    const anchorOrderSet = new Set<number>([firstBodyOrder]);
    for (const s of bodyScenes) if (hasVideo(s)) anchorOrderSet.add(s.order);
    for (const order of extraVideoCardOrders[scenario.id] ?? []) {
      if (bodyScenes.some((s) => s.order === order)) anchorOrderSet.add(order);
    }
    const anchorScenes = bodyScenes.filter((s) => anchorOrderSet.has(s.order));
    const lastBodyOrder = bodyScenes[bodyScenes.length - 1].order;

    // 直前の実効モデルと比べて切り替わったカードだけを対象にする。初回 (記録なし) は
    // 記録のみ (初期分割は上の 1. が担当)。シナリオ切替でキーが変わる分も記録のみになる。
    const prevModels = previousCardModelRef.current;
    const changedCards: Array<{ scene: Scenario['scenes'][number]; key: string; model: GenerationModel; cardIndex: number }> = [];
    anchorScenes.forEach((s, idx) => {
      const key = keyOf(s.order);
      const model = resolveCardModel(key);
      if (!model) return;
      const prev = prevModels.get(key);
      prevModels.set(key, model.model_code);
      if (prev !== undefined && prev !== model.model_code) {
        changedCards.push({ scene: s, key, model, cardIndex: idx + 1 });
      }
    });
    if (changedCards.length === 0) return;

    const addedAnchors: number[] = [];
    for (const { scene: anchor, key, model, cardIndex } of changedCards) {
      // 生成済み / 生成中のカードは触らない (動画の実尺が正で、再分割しても意味がない)
      if (hasVideo(anchor) || sceneVideoLoading[key]) continue;

      // 担当範囲: 「特定シーンだけ生成」が有効ならその範囲、無効 (未設定含む) なら本編全体。
      // 追加カードは復元 effect が範囲を入れるまで undefined なので、その間は判定しない。
      const settings = getSceneVideoSettings(key);
      let rangeStart: number;
      let rangeEnd: number;
      if (settings.sceneRangeEnabled === true && settings.sceneRangeStart != null && settings.sceneRangeEnd != null) {
        rangeStart = Number(settings.sceneRangeStart);
        rangeEnd = Number(settings.sceneRangeEnd);
      } else if (settings.sceneRangeEnabled === undefined && anchor.order !== firstBodyOrder) {
        continue;
      } else {
        rangeStart = firstBodyOrder;
        rangeEnd = lastBodyOrder;
      }
      const covered = bodyScenes
        .filter((s) => s.order >= rangeStart && s.order <= rangeEnd)
        .map((s) => ({ order: s.order, seconds: effDur(s) }));
      if (covered.length === 0) continue;

      const durations = resolveSupportedDurations(model);
      const maxDuration = durations[durations.length - 1];
      const next = segmentScenesByMaxDuration(covered, maxDuration);
      if (next.length <= 1) continue;

      // 分割元カード: 先頭セグメントへ範囲を縮め、「特定シーンだけ生成」を開いた状態にする。
      // 尺は範囲合計をカバーする最小の対応尺 (SceneVideoBox.applySceneRange と同じ)。
      const head = next[0];
      updateSceneVideoSettings(key, {
        sceneRangeEnabled: true,
        sceneRangeStart: head.start,
        sceneRangeEnd: head.end,
        duration: roundToSupportedDuration(head.seconds, durations),
      });

      // 残りのセグメントは追加カードにし、既定範囲を getAutoSegment 経由で渡す
      // (SceneItem の復元 effect が範囲と尺を適用する)。再マウント時にも同じカードが
      // 復元されるよう分割結果はキャッシュへ差し込む。
      autoSegmentCache.set(
        scenario.id,
        mergeSegments(autoSegmentCache.get(scenario.id) ?? [], { start: rangeStart, end: rangeEnd }, next),
      );
      for (const seg of next.slice(1)) {
        if (anchorOrderSet.has(seg.start) || addedAnchors.includes(seg.start)) continue;
        addedAnchors.push(seg.start);
        // 新設カードは分割元カードと同じモデル (と解像度) をシーン上書きとして持たせる。
        // 上書きが無いとシナリオ既定 (videoSettings.videoModel) にフォールバックし、
        // 切り替えたモデルと食い違ったカードが生まれる。範囲と尺は書かず、SceneItem の
        // 復元 effect (sceneRangeEnabled 未設定の間は再試行) に任せる。
        // 「前の動画とつなげる」は既定 ON (autoSplitLinkPreviousSettings。切替後のモデルで
        // 方式を解決する)。
        const anchorScene = bodyScenes.find((s) => s.order === seg.start);
        const linkSettings = anchorScene
          ? autoSplitLinkPreviousSettings(anchorScene, resolveLinkPreviousMode(model.model_code, videoModels))
          : null;
        updateSceneVideoSettings(keyOf(seg.start), {
          videoModel: model.model_code,
          ...(settings.resolution ? { resolution: settings.resolution } : {}),
          ...(linkSettings ?? {}),
        });
      }

      const modelName = `${videoModelGroupLabel(model)} ${videoModelButtonLabel(model)}`.trim();
      const rangeLabel = rangeStart === rangeEnd ? `シーン${rangeStart}` : `シーン${rangeStart}〜${rangeEnd}`;
      toast.show(
        `${modelName}は動画1本あたり最大${maxDuration}秒のため、動画${cardIndex} (${rangeLabel}) を${next.length}本に分割しました。`,
        8000,
      );
    }

    if (addedAnchors.length > 0) {
      materializedRef.current.add(scenario.id);
      setExtraVideoCardOrders((prev) => {
        const existing = prev[scenario.id] ?? [];
        const wanted = addedAnchors.filter((order) => !existing.includes(order));
        if (wanted.length === 0) return prev;
        return { ...prev, [scenario.id]: [...existing, ...wanted].sort((a, b) => a - b) };
      });
    }
  });

  const getAutoSegment = (scenarioId: string, anchorOrder: number): AutoVideoSegment | null =>
    autoSegmentCache.get(scenarioId)?.find((seg) => seg.start === anchorOrder) ?? null;

  return { getAutoSegment };
}
